export interface Profile {
    id: number,
    username: string,
    email: string,
    firstName: string,
    lastName: string,
    phoneNumber: string,
    active: boolean,
    roles: string[]
}

export interface FormField {
    edit: boolean,
    updated: boolean,
    updating: boolean,
    error: string
}

export interface RequestResult<T> {
    data: T
}

export interface TextDiff {
    rows: Row[]
}

export interface Row {
    newLine: string,
    oldLine: string,
    tag: "INSERT" | "DELETE" | "CHANGE" | "EQUAL"
}

export interface Section {
    id: string,
    citation: string,
    number: number,
    name: string,
    description: string
}

export interface SectionResponse {
    section: Section
}

export interface Article {
    id: string,
    name: string,
    parentId: string,
    description: string
}

export interface ArticleResponse {
    article: Article,
    parts: Part[],
    sections: Section[]
}

export enum ExportType {
    EXPORT_TO_XML = "EXPORT_TO_XML",
    CERTIFIED_COPY = "CERTIFIED_COPY",
    UPDATES_PRINTOUT = "UPDATES_PRINTOUT",
    PROVISIONS_PRINTOUT = "PROVISIONS_PRINTOUT",
    TITLE_ANALYSIS = "TITLE_ANALYSIS",
    ARTICLE_ANALYSIS = "ARTICLE_ANALYSIS",
    HEADING_ONLY = "HEADING_ONLY"
}

export interface ExportOptions {
    exportType: ExportType,
    citation: string,
    statuteVersion: PrintoutVersion,
    editorialContentPrintoutVersion: PrintoutVersion,
    editorialTypes: EditorialType[],
    hideHeadingsAndAnalyses?: boolean
}

export enum PrintoutVersion {
    LAST_APPROVED_VERSION = "LAST_APPROVED_VERSION",
    WORKING_COPY_VERSION = "WORKING_COPY_VERSION",
    NONE = "NONE"
}

export interface Title {
    id: number,
    ordinal: number,
    titleName: string,
    titleNumber: string,
    titleStatus: TitleStatus
}

export interface CrsSettings {
    lastPublishedDate: Date | undefined,
    sessionStartDate?: Date | undefined
}

export type TableMeta = {
    rowData: any,
    rowIndex: number
}

export enum TitleStatus {
    WAITING_FOR_ASSIGNMENT = "WAITING_FOR_ASSIGNMENT",
    ASSIGNED = "ASSIGNED",
    IN_PROGRESS = "IN_PROGRESS",
    LE_PROOF = "LE_PROOF",
    ATTORNEY_REVIEW = "ATTORNEY_REVIEW",
    LE_REVIEW_OF_ATTORNEY_CHANGES = "LE_REVIEW_OF_ATTORNEY_CHANGES",
    PUB_TEAM_REVIEW = "PUB_TEAM_REVIEW",
    READY_TO_PUBLISH = "READY_TO_PUBLISH",
    PUBLISH = "PUBLISH"
}

export function createDefaultTitle(): Title {
    return {
        id: 0,
        titleName: "",
        titleNumber: "",
        ordinal: 0,
        titleStatus: TitleStatus.WAITING_FOR_ASSIGNMENT
    }
}

export interface EditorialTemplate {
    id: number,
    title: string,
    content: string,
    editorialType: EditorialType,
    templateOrder: number
}

export interface TitleDivision {
    id: string,
    parentId: string,
    name: string
}

export interface RedbookResponse {
    records: RedBook[],
    pageNumber: number,
    pageSize: number,
    total: number
}

export interface RedBook {
    id: string,
    footnote: string,
    crs: string,
    change: string,
    billNo: string,
    chapter: string,
    billSection: string,
    effectiveDate: string,
    page: number
}

export interface Part {
    id: string,
    name: string,
    parentId: string
}

export interface NavigationListItem {
    id: string,
    text: string,
    component?: JSX.Element,
    onClick: (e: any) => void
}

export interface SaveSectionRequest {
    content: string
}

export interface SearchRequest {
    expression: string
}

export interface ProfileUpdateRequest {
    email: string,
    firstName: string,
    lastName: string,
    phoneNumber: string
}

export interface AuthenticationResponse {
    token: string,
    user: User
}

export interface BillResponse {
    records: BillRecord[],
    pageNumber: number,
    pageSize: number,
    total: number
}

export interface SnackbarState {
    message: string,
    open: boolean,
    severity: "success" | "error"
}

export interface BillRecord {
    billNo: string,
    llsNo: string,
    sponsors: string,
    topic: string,
    status: string,
    drafter: string,
    fiscalAnalyst: string,
    jbcStaff: string,
    currQueue: string,
    billType: string,
    subject: string,
    category: string
}

export enum NodeTypeName {
    TITLE = "TITLE",
    DIVISION = "DIVISION",
    DIVISION_PART = "DIVISION_PART",
    ARTICLE = "ARTICLE",
    ARTICLE_PART = "ARTICLE_PART",
    ARTICLE_SUBPART = "ARTICLE_SUBPART",
    SECTION = "SECTION",
    SUBSECTION = "SUBSECTION",
    PARAGRAPH = "PARAGRAPH",
    SUBPARAGRAPH = "SUBPARAGRAPH",
    SUB_SUBPARAGRAPH = "SUB_SUBPARAGRAPH"
}

export function getNodeTypeLabel(nodeType: NodeTypeName): string {
    switch (nodeType) {
        case NodeTypeName.TITLE: return "Title";
        case NodeTypeName.DIVISION: return "Division";
        case NodeTypeName.DIVISION_PART: return "Subdivision";
        case NodeTypeName.ARTICLE: return "Article";
        case NodeTypeName.ARTICLE_PART: return "Article Part";
        case NodeTypeName.ARTICLE_SUBPART: return "Article Subpart";
        case NodeTypeName.SECTION: return "Section";
        case NodeTypeName.SUBSECTION: return "Subsection";
        case NodeTypeName.PARAGRAPH: return "Paragraph";
        case NodeTypeName.SUBPARAGRAPH: return "Subparagraph";
        case NodeTypeName.SUB_SUBPARAGRAPH: return "Sub-subparagraph";
        default: return "";
    }
}

export enum AnnoNodeTypeName {
    ANNOTATION = "ANNOTATION",
    HEADING = "HEADING",
    DIVISION = "DIVISION",
    SUBDIVISION = "SUBDIVISION",
    SUBSUBDIVISION = "SUBSUBDIVISION",
    CONTENT = "ANNOTEXT"
}

export interface Node {
    nodeId: number,
    parentId: number,
    titleNumber?: number,
    nodeType: NodeTypeName,
    status: NodeStatus,
    ordinal?: number,
    citationPart: string,
    citation: string,
    heading: string,
    content: string,
    lastApprovedDate?: Date,
    nodeHistory?: NodeHistory,
    editorialContent: EditorialContent[],
    annotations?: Annotation,
    hasUnapprovedEditorialContent: boolean,
    markedForDeletion: boolean,
    repealed: boolean,
    workingCopyCitation?: string,
    belowEditorialLevel: boolean
}

export interface EditorialContent {
    approvedCopy: string,
    workingCopy: string,
    editorialType: EditorialType,
    status: EditorialStatus,
    markedForDeletion: boolean,
    lastApprovedDate?: Date
}

export interface AnnoNode {
    nodeId?: number,
    annoId?: number,
    parentId?: number,
    type?: string,
    ordinal: number,
    approvedCopy: string,
    workingCopy: string,
    hasContent?: boolean,
    markedForDeletion: boolean,
    subTreeHasUnapprovedChanges: boolean,
    nodeHasChanges: boolean,
    children: AnnoNode[],
    status?: EditorialStatus,
    lastApprovedDate?: Date
}

export interface Annotation {
    ordinal: number,
    approvedCopy: string,
    workingCopy: string,
    hasContent: boolean,
    markedForDeletion: boolean,
    status?: EditorialStatus,
    subTreeHasUnapprovedChanges: boolean,
    nodeHasChanges: boolean,
    children: AnnoNode[],
    lastApprovedDate?: Date
}

export enum ContentType {
    NODE = "NODE",
    EDITORIAL_CONTENT = "EDITORIAL_CONTENT",
    ANNOTATION = "ANNOTATION"
}

export interface ContentMarkedForDeletion {
    contentType: ContentType,
    node: Node,
    editorialContent: EditorialContent | undefined,
    annotation: Annotation | undefined
}

export interface NodeHistory {
    content: string,
    heading: string,
    citation: string
}

export interface NodeMetaData {
    subTreeHasChanges: boolean
}

export interface TitleTree {
    nodeId: number,
    parentId: number,
    ordinal: number,
    subTreeHasUnapprovedChanges: boolean,
    nodeHasChanges: boolean,
    workingCopyHasChanges: boolean,
    editorialHasChanges: boolean,
    headingHasChanges: boolean,
    nodeType: NodeTypeName,
    citation: string,
    citationPart: string,
    heading: string,
    workingCopyHeading: string | undefined,
    workingCopyCitation: string | undefined,
    children: TitleTree[],
    repealed: boolean,
    belowEditorialLevel: boolean,
    lastApprovedDate?: Date,
}

export interface ArticleResponse {
    article: Article
}

export interface Paragraph {
    paragraphId: number;
    subsectionId: number;
    citation: string;
    marker: string;
    content: string;
}

export interface SubParagraph {
    subparagraphId: number,
    paragraphId: number,
    citation: string,
    marker: string,
    content: string
}

export interface SubSubParagraph {
    subSubparagraphId: number,
    subparagraphId: number,
    citation: string,
    marker: string,
    content: string
}

export type ComponentType = Section | Paragraph | SubParagraph | SubSubParagraph | Article;

export enum NodeStatus {
    APPROVED = "APPROVED",
    APPROVED_BY_PUB_ADMIN = "APPROVED_BY_PUB_ADMIN",
    CHANGED_NOT_APPROVED = "CHANGED_NOT_APPROVED"
}


export enum EditorialType {
    ANALYSIS = "ANALYSIS",
    SOURCE_NOTE = "SOURCE_NOTE",
    EDITORS_NOTE = "EDITORS_NOTE",
    CROSS_REFERENCES = "CROSS_REFERENCES",
    COMMENT = "COMMENT",
    ANNOTATION = "ANNOTATION",
    FORM_INDEX = "FORM_INDEX",
    INDEX = "INDEX",
    ANALYSIS_COMMENT = "ANALYSIS_COMMENT",
    INLINE_EDITOR = "INLINE_EDITOR"
}

export function getEditorialTypeLabel(editorialType: EditorialType): string {
    switch (editorialType) {
        case EditorialType.ANALYSIS: return "Analysis";
        case EditorialType.SOURCE_NOTE: return "Source Note";
        case EditorialType.EDITORS_NOTE: return "Editors Note";
        case EditorialType.CROSS_REFERENCES: return "Cross References";
        case EditorialType.COMMENT: return "Comment";
        case EditorialType.ANNOTATION: return "Annotation";
        case EditorialType.FORM_INDEX: return "Form Index";
        case EditorialType.INDEX: return "Index";
        case EditorialType.ANALYSIS_COMMENT: return "Analysis Comment";
        case EditorialType.INLINE_EDITOR: return "Inline Editor";
        default: return "";
    }
}

export const AllEditorialTypes = [
    EditorialType.ANALYSIS,
    EditorialType.SOURCE_NOTE,
    EditorialType.EDITORS_NOTE,
    EditorialType.CROSS_REFERENCES,
    EditorialType.COMMENT,
    EditorialType.ANNOTATION,
    EditorialType.FORM_INDEX,
    EditorialType.INDEX,
    EditorialType.ANALYSIS_COMMENT,
    EditorialType.INLINE_EDITOR
];

export enum UserRole {
    ADMINISTRATOR = "ADMINISTRATOR",
    PUBLICATIONS_TEAM = "PUBLICATIONS_TEAM",
    NON_PUBLICATIONS_TEAM = "NON_PUBLICATIONS_TEAM"
}

export function getRoleLabel(role: UserRole): string {
    switch (role) {
        case UserRole.ADMINISTRATOR:
            return "Pub Administrator";
        case UserRole.NON_PUBLICATIONS_TEAM:
            return "Non-Pub Team";
        case UserRole.PUBLICATIONS_TEAM:
            return "Pub Team";
        default:
            return "";
    }
}

export interface User {
    id: number,
    username: string,
    email: string,
    firstName: string,
    lastName: string,
    phoneNumber: string,
    userActive: boolean,
    userRole: UserRole,
    lastLoginDate: Date,
    registeredDate: Date
}

export enum EditorialStatus {
    APPROVED = "APPROVED",
    NOT_APPROVED = "NOT_APPROVED"
}

export interface RedBookRecord {
    id: string,
    footnote: string,
    crs: string,
    change: string,
    billNo: string,
    chapter: string,
    billSection: string,
    effectiveDate: string,
    page: number
}

export interface MergeFailure {
    billNum: string,
    billSection: string,
    citation: string,
    action: string,
    errorMsg: string
}

export interface MergeResult {
    recordsUpdated: number,
    failToMergeRecords: MergeFailure[]
}

export interface PagedResponse<T> {
    records: T[],
    pageNumber: number,
    pageSize: number,
    total: number
}

export interface Citation {
    fullCitation: string,
    lastPart: string,
    suggestedHeading: string
}

export interface ServerVersionResponse {
    build: {
        artifact: string,
        name: string,
        time: string,
        version: string,
        group: string
    };
}
